import { IModel, IResourceContext } from '../base/annotation';
import { LinkedService } from '../linkedservice/linkedservice';
import { IIdentityProvider } from './identity-provider';

export interface IAdapterConfig {
    // required, this is used to support multiple contexts
    contextData: IResourceContext;

    identityProvider?: IIdentityProvider;
    version?: string;
    replaceIdentitiesForNonConcepts?: boolean;
    removeEncryptedCredentials?: boolean;
    usedForQueryApi?: boolean;
    fastQueue?: boolean;
    inlineDataset?: boolean;
    referenceById?: boolean;
    tolerateInvalidReference?: boolean;
    dismissEmptyUserProperties?: boolean;
    fromSolutionTemplate?: boolean;
    artifactDMTSCredentials?: Map<string, LinkedService>;
    convertToFabric?: boolean;
    linkedServiceNameToExternalConnectionId?: Record<string, string>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    preProcessResource?: (resource: any) => void; // pre-process resources before toModel
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    postProcessResource?: (resource: any) => void; // post-process resources after toResource
}

export interface IAdapterDefinition {
    model: IModel;
    /*
        Default is false
    */
    asynchronous?: boolean;
    /*
        Default is 'default'
    */
    version?: string;

    /**
     * A representative JSON of what attributes it handles
     * A value of '*' says it handles all attributes underneath
     * A value of '?' says it delegates the handling to another adapter.
     * A value of 'secret' says it is a secretBase property
     * A value of type function queries if this object/attribute is handled
     * That adapter is looked by its 'type' attribute if any
     * If there is a base adapter, its adapts contract is merged
     * with the derived class.
     *
     * Record<string, '*' | '?' | 'secret' | undefined | ((...args: unknown[]) => '*' | '?' | 'secret' | undefined | object) | IAdapterDefinition['adapts']>
     */
    adapts?: {};

    /**
     * If the adapter is disabled. If it is, the adapter will not be registered.
     */
    disabled?: boolean;
}

export interface IAdaptation {
    adapterDefinition: IAdapterDefinition;
    adapterConstructor: Function;
}

export class AdapterRegistry {
    private static modelToAdapters = new Map<IModel, IAdaptation[]>();

    public static adaptations(model: IModel): IAdaptation[] | undefined {
        return AdapterRegistry.modelToAdapters.get(model);
    }

    public static adapter(definition: IAdapterDefinition): ClassDecorator {
        if (definition.model.superModels && definition.adapts) {
            const activityModel = definition.model.superModels.find((model) => model.name === 'activity');
            if (activityModel) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (definition.adapts as any).state = '*';
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (definition.adapts as any).onInactiveMarkAs = '*';
            }
        }
        return <TFunction extends Function>(target: TFunction) => {
            AdapterRegistry.createAdapter(definition, target);
        };
    }

    /**
     * You can register a adapter by creating a model dynamically and registering it with a
     * catch all base adapter
     * <code>
     *      export class GenericDatasetAdapter<T extends Dataset, U extends Model.DatasetResource> extends DatasetAdapter extends BaseAdapter<T, U> {
     *      }
     *      const dynamicsModel = Metamodel.createModel(Dataset.MODEL,
     *      {
     *          name: 'MSDynamics',
     *          displayName: ['Dynamics']
     *      }
     *      AdapterRegistry.createAdapter({
     *          model: dynamicsModel
     *      }, GenericDatasetAdapter);
     * </code>
     */
    public static createAdapter(definition: IAdapterDefinition, target: Function): void {
        if (definition.disabled) {
            return;
        }
        const model = definition.model;
        let adaptations = AdapterRegistry.modelToAdapters.get(model);
        if (!adaptations) {
            adaptations = [];
            AdapterRegistry.modelToAdapters.set(model, adaptations);
        }
        adaptations.push({ adapterDefinition : definition, adapterConstructor: target});
    }

}
